import React from 'react';
import { Box, Button, Chip, Container, Stack, Typography } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { motion } from 'framer-motion';
import { useLang } from '../utils/i18n';
import { openScheduleCall } from '../utils/scheduleCall';

const UI = {
  en: {
    welcome: "Welcome to Martín Antúnez's corner of the web",
    h1_a: 'Your go-to ',
    h1_b: 'Healthcare AI ',
    h1_c: 'Engineer',
    sub: 'I design and deploy ML and AI systems for clinical and biomedical data: LLM assistants, RAG search, predictive risk models, and analytics on Azure & GCP — from prototype to production.',
    bullets: [
      { icon: <RocketLaunchIcon />, label: 'Weeks, not quarters' },
      { icon: <AutoAwesomeIcon />, label: 'RAG & Agentic AI' },
      { icon: <QueryStatsIcon />, label: 'Clinical Risk Prediction' },
      { icon: <MonitorHeartIcon />, label: 'Healthcare & Clinical Data' },
      { icon: <WorkspacePremiumIcon />, label: 'Azure Certified' },
    ],
    ctaPrimary: "Let's Talk",
    ctaSecondary: 'See Projects',
  },
  es: {
    welcome: 'Bienvenido al espacio de Martín Antúnez',
    h1_a: 'Tu Ingeniero de ',
    h1_b: 'IA en Salud ',
    h1_c: 'de confianza',
    sub: 'Diseño e implemento sistemas de ML e IA para datos clínicos y biomédicos: asistentes con LLMs, búsqueda RAG, modelos predictivos de riesgo y analítica en Azure y GCP — del prototipo a producción.',
    bullets: [
      { icon: <RocketLaunchIcon />, label: 'Semanas, no trimestres' },
      { icon: <AutoAwesomeIcon />, label: 'RAG e IA Agéntica' },
      { icon: <QueryStatsIcon />, label: 'Predicción Clínica de Riesgo' },
      { icon: <MonitorHeartIcon />, label: 'Salud y Datos Clínicos' },
      { icon: <WorkspacePremiumIcon />, label: 'Certificado Azure' },
    ],
    ctaPrimary: 'Hablemos',
    ctaSecondary: 'Ver proyectos',
  },
};

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const item = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
};

export default function Hero() {
  const [lang] = useLang();
  const t = UI[lang] || UI.en;

  return (
    <Box
      sx={{
        position: 'relative',
        color: '#e2e8f0',
        py: { xs: 7, md: 16 },
        px: { xs: 2, md: 0 },
        background:
          'radial-gradient(ellipse 1400px 700px at 15% -5%, rgba(6,182,212,0.12), transparent 55%), linear-gradient(180deg, #0a1628 0%, #060c14 100%)',
        overflow: 'hidden',
      }}
    >
      {/* glow orb top-right */}
      <Box sx={{
        position: 'absolute',
        width: 360,
        height: 360,
        right: -100,
        top: -100,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(6,182,212,0.18) 0%, transparent 65%)',
        filter: 'blur(40px)',
        pointerEvents: 'none',
      }} />

      {/* glow orb bottom-left */}
      <Box sx={{
        position: 'absolute',
        width: 280,
        height: 280,
        left: -60,
        bottom: -60,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(125,211,252,0.08) 0%, transparent 65%)',
        filter: 'blur(40px)',
        pointerEvents: 'none',
      }} />

      {/* EEG waveform accent — nods to the neuroscience background */}
      <Box
        component="svg"
        viewBox="0 -20 1440 170"
        preserveAspectRatio="none"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 0,
          bottom: -14,
          width: '100%',
          height: { xs: 70, sm: 95, md: 130 },
          opacity: { xs: 0.25, md: 0.35 },
          pointerEvents: 'none',
          filter: 'drop-shadow(0 0 6px rgba(6,182,212,0.6))',
        }}
      >
        <path
          d="M0.0,60.0 C8.6,60.8 34.3,66.3 51.4,65.0 C68.6,63.7 85.7,51.2 102.9,52.0 C120.0,52.8 137.1,69.7 154.3,70.0 C171.4,70.3 188.6,54.3 205.7,54.0 C222.9,53.7 240.0,71.2 257.1,68.0 C274.3,64.8 291.4,31.3 308.6,35.0 C325.7,38.7 342.9,89.2 360.0,90.0 C377.1,90.8 394.3,54.2 411.4,40.0 C428.6,25.8 445.7,-6.3 462.9,5.0 C480.0,16.3 497.1,105.8 514.3,108.0 C531.4,110.2 548.6,16.0 565.7,18.0 C582.9,20.0 600.0,116.0 617.1,120.0 C634.3,124.0 651.4,48.3 668.6,42.0 C685.7,35.7 702.9,87.3 720.0,82.0 C737.1,76.7 754.3,6.2 771.4,10.0 C788.6,13.8 805.7,103.0 822.9,105.0 C840.0,107.0 857.1,20.8 874.3,22.0 C891.4,23.2 908.6,113.3 925.7,112.0 C942.9,110.7 960.0,26.3 977.1,14.0 C994.3,1.7 1011.4,27.3 1028.6,38.0 C1045.7,48.7 1062.9,76.0 1080.0,78.0 C1097.1,80.0 1114.3,52.0 1131.4,50.0 C1148.6,48.0 1165.7,65.7 1182.9,66.0 C1200.0,66.3 1217.1,52.2 1234.3,52.0 C1251.4,51.8 1268.6,64.7 1285.7,65.0 C1302.9,65.3 1320.0,54.2 1337.1,54.0 C1354.3,53.8 1371.4,63.0 1388.6,64.0 C1405.7,65.0 1431.4,60.7 1440.0,60.0"
          fill="none"
          stroke="#06B6D4"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>

      <Container maxWidth="lg">
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0 }}
        >
          {/* Welcome line */}
          <motion.div variants={item}>
            <Typography
              sx={{
                fontSize: { xs: 17, md: 20 },
                color: 'rgba(226,232,240,0.45)',
                fontStyle: 'italic',
                mb: 2,
              }}
            >
              {t.welcome}
            </Typography>
          </motion.div>

          {/* Headline */}
          <motion.div variants={item} style={{ width: '100%' }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                letterSpacing: -2,
                lineHeight: 1.05,
                fontSize: { xs: 30, sm: 52, md: 68 },
                mb: 3,
              }}
            >
              {t.h1_a}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(90deg, #06B6D4 0%, #7dd3fc 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  display: 'inline',
                }}
              >
                {t.h1_b}
              </Box>
              {t.h1_c}
            </Typography>
          </motion.div>

          {/* Subheadline */}
          <motion.div variants={item} style={{ width: '100%' }}>
            <Typography
              sx={{
                maxWidth: 680,
                mx: 'auto',
                fontSize: { xs: 16, md: 18 },
                color: 'rgba(226,232,240,0.72)',
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              {t.sub}
            </Typography>
          </motion.div>

          {/* Proof points */}
          <motion.div variants={item}>
            {/* Mobile: single wrapping row */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mb: 5 }}>
              {t.bullets.map((b, i) => (
                <Chip key={i} icon={b.icon} label={b.label} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(226,232,240,0.9)', border: '1px solid rgba(255,255,255,0.12)', fontWeight: 600, '& .MuiChip-icon': { color: '#06B6D4' } }} />
              ))}
            </Box>
            {/* Desktop: two rows */}
            <Stack sx={{ display: { xs: 'none', md: 'flex' }, mb: 5 }} spacing={1.5} alignItems="center">
              <Stack direction="row" spacing={1.5} justifyContent="center">
                {t.bullets.slice(0, 3).map((b, i) => (
                  <Chip key={i} icon={b.icon} label={b.label} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(226,232,240,0.9)', border: '1px solid rgba(255,255,255,0.12)', fontWeight: 600, '& .MuiChip-icon': { color: '#06B6D4' } }} />
                ))}
              </Stack>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                {t.bullets.slice(3).map((b, i) => (
                  <Chip key={i} icon={b.icon} label={b.label} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(226,232,240,0.9)', border: '1px solid rgba(255,255,255,0.12)', fontWeight: 600, '& .MuiChip-icon': { color: '#06B6D4' } }} />
                ))}
              </Stack>
            </Stack>
          </motion.div>

          {/* CTAs */}
          <motion.div variants={item}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: { xs: 3, md: 5 } }}>
              <Button
                onClick={openScheduleCall}
                variant="contained"
                color="primary"
                size="large"
                startIcon={<CalendarMonthIcon />}
                aria-label={t.ctaPrimary}
                sx={{ px: 4, py: 1.5, fontSize: 16 }}
              >
                {t.ctaPrimary}
              </Button>
              <Button
                component="a"
                href="/projects"
                variant="outlined"
                size="large"
                sx={{
                  color: 'rgba(226,232,240,0.85)',
                  borderColor: 'rgba(255,255,255,0.18)',
                  px: 4,
                  py: 1.5,
                  fontSize: 16,
                  '&:hover': { borderColor: '#06B6D4', color: '#06B6D4' },
                }}
              >
                {t.ctaSecondary}
              </Button>
            </Stack>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}
